<template>
    <div>
        <AdminPageHeader
            :title="header.title"
            :description="header.description"
        />

        <!-- Inicio: accesos directos a los módulos. La navegación completa
             vive en el sidebar; esto es sólo la portada del panel. -->
        <div v-if="view === 'inicio'" class="admin-home">
            <router-link
                v-for="shortcut in shortcuts"
                :key="shortcut.label"
                :to="shortcut.to"
                class="admin-shortcut"
            >
                <span class="admin-shortcut__icon">
                    <component :is="shortcut.icon" :size="20" />
                </span>
                <span class="admin-shortcut__text">
                    <strong>{{ shortcut.label }}</strong>
                    <span class="adm-muted">{{ shortcut.hint }}</span>
                </span>
            </router-link>
        </div>

        <AdmCard v-else-if="view === 'empresa'" title="Información de la empresa"
                 subtitle="Estos datos alimentan el pie de página y los documentos de la web pública.">
            <form class="admin-form" @submit.prevent="saveCompany">
                <AdmField v-model="form.name" label="Nombre de la empresa" placeholder="Selk S.A.S." />
                <AdmField v-model="form.address" label="Dirección" placeholder="Calle 1 #23-45" />
                <AdmField v-model="form.phone" label="Teléfono" placeholder="+57 300 000 0000" />
                <AdmField v-model="form.email" label="Correo electrónico" type="email" placeholder="contacto@selk.com.co" />
                <div class="admin-form__actions">
                    <AdmButton type="submit" :loading="saving">Guardar cambios</AdmButton>
                </div>
            </form>
        </AdmCard>

        <AdmCard v-else title="Cambiar contraseña"
                 subtitle="Se aplica al usuario con el que iniciaste sesión.">
            <form class="admin-form" @submit.prevent="savePassword">
                <AdmField
                    v-model="password.value"
                    label="Nueva contraseña"
                    type="password"
                    :error="password.error"
                    hint="Mínimo 8 caracteres."
                />
                <AdmField
                    v-model="password.repeat"
                    label="Repita la contraseña"
                    type="password"
                />
                <div class="admin-form__actions">
                    <AdmButton type="submit" :loading="savingPassword">Actualizar contraseña</AdmButton>
                </div>
            </form>
        </AdmCard>
    </div>
</template>

<script>
/**
 * Panel administrativo — migrado en el plan 27_09_2026 (F3/T17).
 *
 * Antes esta vista cargaba el menú principal: tres `.menu-group` con todos
 * los enlaces del panel, duplicados carácter a carácter en Gastos e Ingresos.
 * Esa navegación vive ahora en el sidebar (`config/adminNav.js`) y aquí sólo
 * queda la portada más los dos formularios de configuración.
 *
 * Defecto corregido al separar estado de vista y datos: `this.company`
 * hacía de objeto de empresa Y de interruptor del formulario a la vez, así
 * que `companyAdmin()` lo sobrescribía con `true` y `saveCompany()` acababa
 * leyendo `true.id` (undefined) al guardar después de cambiar de pestaña.
 * Ahora la vista activa se deriva de la URL (`?vista=`) y la empresa vive en
 * su propia propiedad.
 */
import { Building2, UserCog, LayoutDashboard, TrendingUp, TrendingDown, Inbox } from 'lucide-vue-next';
import AdminPageHeader from '@/components/Admin/AdminPageHeader.vue';
import AdmCard from '@/components/Admin/ui/AdmCard.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import { notify } from '@/composables/useAdmToast.js';
import { getUserCompany, updateCompanyInfo, updatePassword } from '@/services/dbConsults.js';

const MIN_PASSWORD_LENGTH = 8;

export default {
    name: 'adminPage',
    components: { AdminPageHeader, AdmCard, AdmField, AdmButton },
    inject: ['reloadCompany'],
    data() {
        return {
            company: null,
            form: { name: '', address: '', phone: '', email: '' },
            password: { value: '', repeat: '', error: '' },
            saving: false,
            savingPassword: false,
            shortcuts: [
                { label: 'Dashboard', hint: 'Indicadores de ingresos y gastos', to: '/dashboard', icon: LayoutDashboard },
                { label: 'Ingresos', hint: 'Registro y catálogos de ingresos', to: '/ingresos', icon: TrendingUp },
                { label: 'Gastos', hint: 'Registro, proveedores y cajas', to: '/gastos', icon: TrendingDown },
                { label: 'Órdenes 3D', hint: 'Bandeja de solicitudes de impresión', to: '/admin/print3d/ordenes', icon: Inbox },
                { label: 'Empresa', hint: 'Datos de contacto del sitio', to: { path: '/admin', query: { vista: 'empresa' } }, icon: Building2 },
                { label: 'Usuarios', hint: 'Cambio de contraseña', to: { path: '/admin', query: { vista: 'usuario' } }, icon: UserCog },
            ],
        };
    },
    computed: {
        /** La sub-vista se deriva de la URL: así el sidebar puede enlazarla. */
        view() {
            const vista = this.$route.query.vista;
            return vista === 'empresa' || vista === 'usuario' ? vista : 'inicio';
        },
        header() {
            if (this.view === 'empresa') {
                return { title: 'Empresa', description: '' };
            }
            if (this.view === 'usuario') {
                return { title: 'Usuarios', description: '' };
            }
            return {
                title: 'Panel administrativo',
                description: 'Accesos directos a los módulos de gestión de Selk.',
            };
        },
    },
    methods: {
        async loadCompany() {
            const { company, error } = await getUserCompany();
            if (error) {
                notify.error(error);
                return;
            }
            this.company = company;
            if (company) {
                this.form = {
                    name: company.name || '',
                    address: company.address || '',
                    phone: company.phone || '',
                    email: company.email || '',
                };
            }
        },
        async saveCompany() {
            if (!this.company?.id) {
                notify.error('No se pudo identificar la empresa a actualizar.');
                return;
            }
            this.saving = true;
            const { error } = await updateCompanyInfo(this.company.id, this.form);
            this.saving = false;

            if (error) {
                notify.error(error);
                return;
            }
            notify.success('Información de la empresa actualizada.');
            await this.loadCompany();
            // Refresca el footer público, que consume estos mismos datos.
            this.reloadCompany();
        },
        async savePassword() {
            this.password.error = '';

            if (!this.password.value || this.password.value.length < MIN_PASSWORD_LENGTH) {
                this.password.error = `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
                return;
            }
            if (this.password.value !== this.password.repeat) {
                this.password.error = 'Las contraseñas no coinciden.';
                return;
            }

            this.savingPassword = true;
            const res = await updatePassword(this.password.value);
            this.savingPassword = false;

            if (!res) {
                notify.error('No se pudo actualizar la contraseña.');
                return;
            }
            notify.success('Contraseña actualizada correctamente.');
            this.password = { value: '', repeat: '', error: '' };
        },
    },
    mounted() {
        this.loadCompany();
    },
};
</script>

<style scoped>
.admin-home {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: var(--adm-gap);
}

.admin-shortcut {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: var(--adm-gap);
    background-color: var(--adm-surface);
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius);
    box-shadow: var(--adm-shadow);
    color: var(--color-text-strong);
    text-decoration: none;
    transition: border-color 0.15s ease, transform 0.15s ease;
}

.admin-shortcut:hover {
    border-color: var(--color-brand);
    transform: translateY(-2px);
}

.admin-shortcut__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: var(--adm-radius-sm);
    background-color: var(--adm-info-bg);
    color: var(--color-brand);
}

.admin-shortcut__text {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
}

.admin-form {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: var(--adm-gap);
    max-width: 720px;
}

.admin-form__actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
}
</style>
